import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Button,
  Heading,
  TextField,
  Toggle,
  Divider,
  Flex,
  Box,
  Text,
  IconButton,
} from '@vibe/core';
import { CloseSmall } from '@vibe/icons';
import { invalidateCapacityCache } from '../../hooks/useWorkload';
import {
  createCapacityScheme,
  updateCapacityScheme,
  getSchemeAssignments,
  createSchemeAssignment,
  removeSchemeAssignment,
  type CapacityScheme,
  type CapacitySchemeAssignment,
  type DaySchedule,
  type CreateCapacitySchemePayload,
  DAY_KEYS,
  DEFAULT_DAY_SCHEDULE,
} from '../../api/capacity.api';
import { fetchMondayUsers } from '../../api/monday.api';
import type { MondayUser } from '../../api/monday.api';
import UserPicker, { type PickedUser } from '../shared/UserPicker';

// ─── Day label mapping ──────────────────────────────────────────────────────

const DAY_LABELS: Record<keyof DaySchedule, { label: string; short: string }> = {
  mon: { label: 'Monday', short: 'Mon' },
  tue: { label: 'Tuesday', short: 'Tue' },
  wed: { label: 'Wednesday', short: 'Wed' },
  thu: { label: 'Thursday', short: 'Thu' },
  fri: { label: 'Friday', short: 'Fri' },
  sat: { label: 'Saturday', short: 'Sat' },
  sun: { label: 'Sunday', short: 'Sun' },
};

// ─── Props ──────────────────────────────────────────────────────────────────

interface CapacitySchemeEditorProps {
  /** Existing scheme to edit, or null for creating a new one */
  scheme: CapacityScheme | null;
  /** Called when the user saves or cancels */
  onSave: (saved: CapacityScheme) => void;
  onCancel: () => void;
}

// ─── Component ──────────────────────────────────────────────────────────────

const CapacitySchemeEditor: React.FC<CapacitySchemeEditorProps> = ({
  scheme,
  onSave,
  onCancel,
}) => {
  const isEditing = scheme !== null;

  // Form state
  const [name, setName] = useState(scheme?.name ?? '');
  const [schedule, setSchedule] = useState<DaySchedule>(
    scheme?.schedule ?? { ...DEFAULT_DAY_SCHEDULE },
  );
  const [isDefault, setIsDefault] = useState(scheme?.is_default ?? false);

  // Assignments state
  const [assignments, setAssignments] = useState<CapacitySchemeAssignment[]>([]);

  // All users cache (for resolving names of already-assigned users)
  const [allUsersCache, setAllUsersCache] = useState<MondayUser[]>([]);

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch all users from monday.com on mount (for name resolution)
  useEffect(() => {
    let cancelled = false;
    const loadData = async () => {
      try {
        const users = await fetchMondayUsers();
        if (!cancelled) setAllUsersCache(users);
      } catch (err) {
        console.error('[CapacitySchemeEditor] Failed to fetch monday data:', err);
      }
    };
    loadData();
    return () => { cancelled = true; };
  }, []);

  // Fetch existing assignments when editing
  useEffect(() => {
    if (!scheme) return;
    let cancelled = false;
    const fetchAssignments = async () => {
      try {
        const data = await getSchemeAssignments(scheme.id);
        if (!cancelled) setAssignments(data);
      } catch (err) {
        console.error('[CapacitySchemeEditor] Failed to fetch assignments:', err);
      }
    };
    fetchAssignments();
    return () => { cancelled = true; };
  }, [scheme]);

  // Compute average working hours
  const workingDays = DAY_KEYS.filter((k) => schedule[k] > 0);
  const totalHours = DAY_KEYS.reduce((sum, k) => sum + schedule[k], 0);
  const avgHoursPerDay = workingDays.length > 0
    ? totalHours / workingDays.length
    : 0;

  const handleDayHoursChange = useCallback(
    (dayKey: keyof DaySchedule, value: string) => {
      const hours = parseFloat(value);
      if (isNaN(hours) || hours < 0 || hours > 24) return;
      setSchedule((prev) => ({ ...prev, [dayKey]: hours }));
    },
    [],
  );

  const handleAddUserAssignment = useCallback(
    async (user: PickedUser | null) => {
      console.log('[CapacitySchemeEditor] handleAddUserAssignment', { user, scheme: scheme?.id, assignmentsCount: assignments.length });
      if (!user || !scheme) return;
      const userId = user.id;
      if (assignments.some((a) => a.monday_user_id === userId)) return;
      try {
        const created = await createSchemeAssignment(scheme.id, {
          mondayUserId: userId,
        });
        setAssignments((prev) => [...prev, created]);
        invalidateCapacityCache();
      } catch (err) {
        console.error('[CapacitySchemeEditor] Failed to add user assignment:', err);
      }
    },
    [scheme, assignments],
  );

  const handleRemoveAssignment = useCallback(
    async (assignmentId: string) => {
      if (!scheme) return;
      try {
        await removeSchemeAssignment(scheme.id, assignmentId);
        setAssignments((prev) => prev.filter((a) => a.id !== assignmentId));
        invalidateCapacityCache();
      } catch (err) {
        console.error('[CapacitySchemeEditor] Failed to remove assignment:', err);
      }
    },
    [scheme],
  );

  const handleSave = useCallback(async () => {
    if (!name.trim()) {
      setError('Scheme name is required');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const payload: CreateCapacitySchemePayload = {
        name: name.trim(),
        schedule,
        isDefault,
      };

      let saved: CapacityScheme;
      if (isEditing && scheme) {
        saved = await updateCapacityScheme(scheme.id, payload);
      } else {
        saved = await createCapacityScheme(payload);
      }
      invalidateCapacityCache();
      onSave(saved);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to save scheme';
      setError(message);
    } finally {
      setIsSaving(false);
    }
  }, [name, schedule, isDefault, isEditing, scheme, onSave]);

  // Helpers for assignment display
  const userAssignments = assignments.filter((a) => a.monday_user_id != null);

  const getUserName = (userId: string) => {
    const user = allUsersCache.find((u) => String(u.id) === userId);
    return user?.name ?? `User #${userId}`;
  };
  const getUserAvatar = (userId: string) => {
    const user = allUsersCache.find((u) => String(u.id) === userId);
    return user?.photo_thumb_small ?? null;
  };

  // User IDs already assigned (for UserPicker exclusion)
  const excludeUserIds = useMemo(
    () => userAssignments.map((a) => a.monday_user_id!),
    [userAssignments],
  );

  return (
    <div className="rp-capacity-scheme-editor">
      {/* Header */}
      <Heading type={Heading.types.H3}>
        {isEditing ? 'Edit Capacity Scheme' : 'New Capacity Scheme'}
      </Heading>

      <div className="rp-capacity-scheme-editor__body">
        {/* Name */}
        <Box className="rp-capacity-scheme-editor__field">
          <label className="rp-capacity-scheme-editor__label">
            Scheme Name
          </label>
          <TextField
            size={TextField.sizes.SMALL}
            value={name}
            onChange={(value: string) => setName(value)}
            placeholder="e.g. Full-time, Part-time 4h"
            className="rp-capacity-scheme-editor__input"
          />
        </Box>

        <Divider />

        {/* Weekly schedule grid */}
        <Box className="rp-capacity-scheme-editor__field">
          <label className="rp-capacity-scheme-editor__label">
            Weekly Work Hours
          </label>
          <div className="rp-capacity-scheme-editor__week-grid">
            {DAY_KEYS.map((dayKey) => (
              <div key={dayKey} className="rp-capacity-scheme-editor__day">
                <Text
                  type={Text.types.TEXT2}
                  weight={Text.weights.MEDIUM}
                  className="rp-capacity-scheme-editor__day-label"
                >
                  {DAY_LABELS[dayKey].short}
                </Text>
                <TextField
                  type={TextField.types.NUMBER}
                  size={TextField.sizes.SMALL}
                  value={String(schedule[dayKey])}
                  onChange={(value: string) =>
                    handleDayHoursChange(dayKey, value)
                  }
                  className="rp-capacity-scheme-editor__day-input"
                />
                <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
                  h
                </Text>
              </div>
            ))}
          </div>
          <Text
            type={Text.types.TEXT2}
            style={{ color: '#676879', marginTop: '8px' }}
          >
            Average: {avgHoursPerDay.toFixed(1)}h / working day
            ({workingDays.length} days, {totalHours}h total)
          </Text>
        </Box>

        <Divider />

        {/* Set as default */}
        <Box className="rp-capacity-scheme-editor__field">
          <Flex
            justify={Flex.justify.SPACE_BETWEEN}
            align={Flex.align.CENTER}
          >
            <div>
              <Text type={Text.types.TEXT1} weight={Text.weights.MEDIUM}>
                Set as Default
              </Text>
              <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
                Unassigned users will use this scheme automatically
              </Text>
            </div>
            <Toggle
              isSelected={isDefault}
              onChange={(checked: boolean) => setIsDefault(checked)}
              size="small"
              ariaLabel="Set as default scheme"
            />
          </Flex>
        </Box>

        <Divider />

        {/* User Assignments */}
        {isEditing && (
          <Box className="rp-capacity-scheme-editor__field">
            <Text type={Text.types.TEXT1} weight={Text.weights.MEDIUM}>
              Assigned Users
            </Text>
            <Text
              type={Text.types.TEXT2}
              style={{ color: '#676879', marginBottom: '8px' }}
            >
              Users directly assigned to this scheme
            </Text>

            {userAssignments.length > 0 && (
              <div className="rp-capacity-scheme-editor__assignment-list">
                {userAssignments.map((a) => (
                  <div
                    key={a.id}
                    className="rp-capacity-scheme-editor__assignment-chip"
                  >
                    <Avatar
                      size={Avatar.sizes.SMALL}
                      type={getUserAvatar(a.monday_user_id!) ? "img" : "text"}
                      src={getUserAvatar(a.monday_user_id!) || undefined}
                      text={getUserName(a.monday_user_id!).charAt(0).toUpperCase()}
                      ariaLabel={getUserName(a.monday_user_id!)}
                    />
                    <Text type={Text.types.TEXT2}>
                      {getUserName(a.monday_user_id!)}
                    </Text>
                    <IconButton
                      icon={CloseSmall}
                      size={IconButton.sizes.XXS}
                      onClick={() => handleRemoveAssignment(a.id)}
                      ariaLabel="Remove assignment"
                    />
                  </div>
                ))}
              </div>
            )}

            <UserPicker
              value={null}
              onChange={handleAddUserAssignment}
              placeholder="Search for a user to add…"
              excludeUserIds={excludeUserIds}
            />
          </Box>
        )}

        {!isEditing && (
          <Box className="rp-capacity-scheme-editor__field">
            <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
              Save the scheme first, then assign users and teams.
            </Text>
          </Box>
        )}
      </div>

      {/* Footer */}
      <div className="rp-capacity-scheme-editor__footer">
        {error && (
          <Text style={{ color: '#e2445c', marginBottom: '8px' }}>
            {error}
          </Text>
        )}
        <Flex gap={Flex.gaps.SMALL} justify={Flex.justify.END}>
          <Button kind={Button.kinds.TERTIARY} onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={isSaving}>
            {isEditing ? 'Save Changes' : 'Create Scheme'}
          </Button>
        </Flex>
      </div>
    </div>
  );
};

export default CapacitySchemeEditor;
